import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatMoney } from '@makila/fiscal';
import { leaveQueue, newQueueTicket, queueEstimates, saveOrder, startService, ticketMinutes, type CatalogItem, type Customer, type LicenceStatus, type Order, type Signer, orderTotal } from '@makila/pos-core';
import { CalendarDays, Check, Clock, LayoutDashboard, ListOrdered, MessageCircle, Moon, Plug, Plus, RefreshCw, ShoppingCart, Sun, Undo2, UserPlus, Wallet, X } from 'lucide-react';
import clsx from 'clsx';
import { CustomerDialog } from '../components/CustomerDialog';
import { HardwareDialog } from '../components/HardwareDialog';
import { Rail, RailItem, RailTool } from '../components/Rail';
import { type Staff, StaffAvatar, staffPrice } from '../components/StaffPicker';
import { StatusPill } from '../components/StatusPill';
import { type DeviceState, identity } from '../lib/device';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { SyncStatus } from '../lib/sync';
import { useTheme } from '../lib/theme';
import type { Operator } from './Lock';
import type { Product } from './Sale';

const data = <T,>(i: CatalogItem) => i.data as unknown as T;
const minutesSince = (iso: string, now: number) => Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
const today = () => new Date().toISOString().slice(0, 10);
const waLink = (n: string, text: string) => `https://wa.me/${n.replace(/\D/g, '').replace(/^(8\d{8})$/, '258$1')}?text=${encodeURIComponent(text)}`;

interface Props {
  store: IdbTerminalStore; device: DeviceState; signer: Signer; licence: LicenceStatus | null; sync: SyncStatus | null; operator: Operator;
  /** "Concluir e cobrar": a venda abre com os serviços e o profissional de cada um. */
  onCharge: (o: Order) => void;
  onCounter: (open?: 'cash' | 'return') => void; onLock: () => void; onSyncNow: () => void; onGestao?: () => void;
  /** Salão com fila e agenda: ir para a agenda. */
  onAgenda?: () => void;
}

/**
 * Fila de espera (docs/14, Fase 1): quem chega sem marcação escolhe o
 * profissional ou "o primeiro livre"; cada profissional vê quem tem à espera
 * e quanto falta. A senha é uma comanda (sincroniza entre equipamentos e
 * funciona sem rede); "Concluir e cobrar" leva-a para a venda.
 */
export function QueueScreen(p: Props) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [services, setServices] = useState<Product[]>([]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hwOpen, setHwOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [dark, toggleTheme] = useTheme();
  const who = identity(p.device);
  const plates = p.device.company.businessType === 'carwash';

  const reload = useCallback(async () => {
    setOrders((await p.store.orders()).filter((o) => o.kind === 'queue'));
    setStaff((await p.store.catalog('operator')).map(data<Staff & { sortOrder?: number }>).filter((o) => o.active && o.performsServices)
      .sort((a, b) => ((a as { sortOrder?: number }).sortOrder ?? 0) - ((b as { sortOrder?: number }).sortOrder ?? 0) || a.name.localeCompare(b.name, 'pt')));
    setServices((await p.store.catalog('product')).map(data<Product>).filter((x) => x.active && x.kind === 'service' && x.posVisible !== false).sort((a, b) => a.name.localeCompare(b.name, 'pt')));
  }, [p.store]);
  useEffect(() => { void reload(); }, [reload, p.sync?.lastSyncAt]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);

  const open = orders.filter((o) => o.status === 'open');
  const estimates = useMemo(() => queueEstimates(open, staff.map((s) => s.id), new Date(now)), [open, staff, now]);
  const save = async (o: Order) => {
    try { await saveOrder(p.store, who, o); p.onSyncNow(); await reload(); setError(null); } catch (e) { setError((e as Error).message); }
  };
  // A senha do dia: a seguir à maior que esta caixa conhece de hoje.
  const nextTicket = 1 + Math.max(0, ...orders.filter((o) => o.openedAt.slice(0, 10) === today()).map((o) => o.queue?.ticket ?? 0));
  const waitingFor = (staffId: string | null) => open.filter((o) => o.queue?.status === 'waiting' && (o.queue.preferredStaffId ?? null) === staffId).sort((a, b) => a.queue!.ticket - b.queue!.ticket);
  const servingBy = (staffId: string) => open.filter((o) => o.queue?.status === 'serving' && o.queue.servingBy === staffId);
  const nWaiting = open.filter((o) => o.queue?.status === 'waiting').length;

  // Os mesmos atalhos da barra principal.
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (document.querySelector('dialog[open]')) return;
      if (e.key === 'F3' || e.key === '+') { e.preventDefault(); setAdding(true); }
      if (e.key === 'F8' && p.licence?.sell) { e.preventDefault(); p.onCounter('return'); }
      if (e.key === 'F9') { e.preventDefault(); p.onCounter('cash'); }
      if (e.key === 'F10' && p.onGestao) { e.preventDefault(); p.onGestao(); }
    };
    addEventListener('keydown', on);
    return () => removeEventListener('keydown', on);
  });

  const card = (o: Order, column: string | null) => {
    const q = o.queue!;
    const serving = q.status === 'serving';
    const est = estimates.get(o.id);
    // O que a venda vai cobrar (IVA de preços sem IVA, modificadores), não preço × quantidade (AUD-21).
    const total = orderTotal(o);
    return (
      <article key={o.id} aria-label={`Senha ${q.ticket} ${q.customer.name}`} className={clsx('rounded-2xl border-2 bg-[var(--surface)] p-3 shadow-xs', serving ? 'border-green-600' : 'border-base')}>
        <div className="flex items-start gap-3">
          <span className={clsx('num grid h-11 w-11 shrink-0 place-items-center rounded-xl text-lg font-bold', serving ? 'bg-green-600 text-white' : 'bg-surface-2')}>{q.ticket}</span>
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">{q.customer.name}{q.plate && <span className="ml-2 rounded bg-ink px-1.5 font-mono text-xs text-white">{q.plate}</span>}</div>
            <div className="truncate text-xs text-muted">{o.items.filter((i) => i.status !== 'cancelled').map((i) => i.description).join(', ') || 'Serviço por escolher'}</div>
          </div>
          <div className="num shrink-0 text-right text-xs text-muted">
            <div className="flex items-center justify-end gap-1"><Clock size={12} />{serving ? `${minutesSince(q.startedAt ?? o.openedAt, now)} min` : `${minutesSince(o.openedAt, now)} min`}</div>
            {!serving && est !== undefined && <div className="font-semibold text-[var(--text)]">{est ? `~${est} min` : 'agora'}</div>}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {serving ? (
            <button onClick={() => p.onCharge(o)} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-brand-700 px-3 font-bold text-white"><Check size={18} /> Concluir e cobrar{total !== '0.00' ? ` · ${formatMoney(total)}` : ''}</button>
          ) : (
            <button onClick={() => {
              const by = column ?? staff.find((s) => !servingBy(s.id).length)?.id ?? null;
              if (!by) { setError('Ninguém está livre: escolha quem atende na coluna do profissional'); return; }
              void save(startService(o, by, new Date()));
            }} className="h-12 flex-1 rounded-xl border-2 border-brand-700 px-3 font-semibold text-brand-700">Atender</button>
          )}
          {q.customer.phone && !serving && (
            <a aria-label={`Avisar ${q.customer.name} por WhatsApp`} target="_blank" rel="noreferrer" href={waLink(q.customer.phone, `Olá ${q.customer.name}, é a sua vez em ${p.device.company.tradeName ?? p.device.company.legalName}.`)}
              className="grid h-12 w-12 place-items-center rounded-xl border border-base text-green-600"><MessageCircle size={18} /></a>
          )}
          {!serving && <button aria-label={`${q.customer.name} saiu`} title="Saiu sem ser atendido" onClick={() => void save(leaveQueue(o, p.operator.id, new Date()))} className="grid h-12 w-12 place-items-center rounded-xl border border-base text-muted"><X size={18} /></button>}
        </div>
      </article>
    );
  };

  const columns: { id: string | null; title: React.ReactNode; sub: string }[] = [
    { id: null, title: <span className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-full bg-surface-2"><ListOrdered size={16} /></span>Primeiro livre</span>, sub: 'Vai para quem acabar primeiro' },
    ...staff.map((s) => {
      const busy = servingBy(s.id)[0];
      return { id: s.id, title: <span className="flex items-center gap-2"><StaffAvatar s={s} size={32} />{s.name}</span>, sub: busy ? `A atender ${busy.queue!.customer.name}` : 'Livre' };
    }),
  ];

  return (
    <div className="flex h-full flex-col-reverse md:flex-row">
      <Rail label="Caixa" footer={<>
        {p.onGestao && <RailItem icon={LayoutDashboard} label="Gestão" kbd="F10" ariaLabel="Abrir a gestão" onClick={p.onGestao} />}
        <RailTool icon={Plug} label="Equipamento" onClick={() => setHwOpen(true)} />
        <RailTool icon={dark ? Sun : Moon} label={dark ? 'Modo claro' : 'Modo escuro'} onClick={toggleTheme} />
        <RailTool icon={RefreshCw} label="Sincronizar agora" onClick={p.onSyncNow} />
        <RailTool label="Trocar de operador" onClick={p.onLock}>
          <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-700 text-sm font-bold text-white">{p.operator.name.slice(0, 1).toUpperCase()}</span>
        </RailTool>
      </>}>
        {p.onAgenda && <RailItem icon={CalendarDays} label="Agenda" onClick={p.onAgenda} />}
        <RailItem icon={ListOrdered} label="Fila" active badge={nWaiting || null} />
        <RailItem icon={ShoppingCart} label="Vender" onClick={() => p.onCounter()} />
        <RailItem icon={Undo2} label="Devolução" kbd="F8" disabled={!p.licence?.sell} onClick={() => p.onCounter('return')} />
        <RailItem icon={Wallet} label="Caixa" kbd="F9" onClick={() => p.onCounter('cash')} />
      </Rail>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex min-h-16 flex-wrap items-center gap-x-4 gap-y-2 border-b border-base bg-[var(--surface)] px-4 py-2">
          <div className="min-w-0">
            <div className="truncate font-display text-[17px] font-semibold leading-tight">Fila de espera</div>
            <div className="truncate text-xs text-muted">{nWaiting} à espera · {open.filter((o) => o.queue?.status === 'serving').length} a ser atendidos</div>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <StatusPill sync={p.sync} />
            <button onClick={() => setAdding(true)} title="Cliente na fila (F3)" className="flex h-12 items-center gap-2 rounded-xl bg-brand-700 px-4 font-bold text-white"><UserPlus size={18} /> Cliente na fila</button>
          </div>
        </header>
        {error && <div role="alert" className="flex items-center gap-2 bg-amber-100 px-4 py-2 text-amber-900"><span className="flex-1">{error}</span><button aria-label="Fechar aviso" onClick={() => setError(null)} className="grid h-10 w-10 place-items-center"><X size={18} /></button></div>}

        <main className="min-h-0 flex-1 overflow-auto p-4">
          {!staff.length ? (
            <div className="grid h-full place-items-center text-center text-muted">
              <p className="max-w-md">Ainda não há profissionais. Na gestão, em Equipa, abra a ficha de cada um e ligue "Faz serviços".</p>
            </div>
          ) : (
            <div className="grid auto-cols-[minmax(260px,1fr)] grid-flow-col gap-4 lg:auto-cols-[minmax(280px,1fr)]">
              {columns.map((c) => (
                <section key={c.id ?? 'free'} aria-label={c.id ? `Fila de ${staff.find((s) => s.id === c.id)?.name}` : 'Primeiro livre'} className="flex min-h-0 flex-col rounded-2xl border border-base bg-black/[.025] p-3 dark:bg-white/[.04]">
                  <div className="mb-3 px-1">
                    <div className="font-semibold">{c.title}</div>
                    <div className={clsx('mt-0.5 text-xs', c.sub === 'Livre' ? 'font-semibold text-green-700 dark:text-green-400' : 'text-muted')}>{c.sub}</div>
                  </div>
                  <div className="space-y-2.5">
                    {c.id && servingBy(c.id).map((o) => card(o, c.id))}
                    {waitingFor(c.id).map((o) => card(o, c.id))}
                    {!waitingFor(c.id).length && !(c.id && servingBy(c.id).length) && <p className="px-1 py-6 text-center text-sm text-muted">Ninguém à espera</p>}
                  </div>
                </section>
              ))}
            </div>
          )}
        </main>
      </div>

      {adding && (
        <NewTicketDialog services={services} staff={staff} store={p.store} device={p.device} plates={plates} ticket={nextTicket}
          onClose={() => setAdding(false)}
          onCreate={async (t) => {
            const o = newQueueTicket({
              id: crypto.randomUUID(), ticket: nextTicket, customer: t.customer, preferredStaffId: t.staffId, plate: t.plate, operatorId: p.operator.id, terminalId: who.terminalId, now: new Date(),
              services: t.services.map((x) => {
                const own = staffPrice(staff.find((s) => s.id === t.staffId), x.id);
                return { itemId: crypto.randomUUID(), productId: x.id, description: x.name, unitPrice: own?.price ?? x.unitPrice, taxRate: x.taxRate, priceIncludesTax: x.priceIncludesTax, exemptionCode: x.exemptionCode, durationMin: own?.durationMin ?? x.durationMin ?? null };
              }),
            });
            await save(o);
            setAdding(false);
          }} />
      )}
      {hwOpen && <HardwareDialog onClose={() => setHwOpen(false)} />}
    </div>
  );
}

/** Novo cliente na fila: nome (ou cliente da ficha), serviços e por quem quer ser atendido. */
function NewTicketDialog(p: {
  services: Product[]; staff: Staff[]; store: IdbTerminalStore; device: DeviceState; plates: boolean; ticket: number; onClose: () => void;
  onCreate: (t: { customer: { id?: string; name: string; phone: string | null }; services: Product[]; staffId: string | null; plate: string | null }) => Promise<void>;
}) {
  const dlg = useRef<HTMLDialogElement>(null);
  useEffect(() => { dlg.current?.showModal(); }, []);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [plate, setPlate] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [staffId, setStaffId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = p.services.filter((x) => !staffId || !p.staff.find((s) => s.id === staffId)?.services?.length || p.staff.find((s) => s.id === staffId)!.services!.some((v) => v.productId === x.id));
  const mins = chosen.reduce((s, id) => s + (p.services.find((x) => x.id === id)?.durationMin ?? 0), 0);
  return (
    <dialog ref={dlg} onCancel={(e) => { e.preventDefault(); p.onClose(); }} className="m-0 h-full max-h-none w-full max-w-none bg-[var(--surface)] p-0 text-[var(--text)] sm:m-auto sm:h-auto sm:max-h-[92vh] sm:w-[min(720px,96vw)] sm:rounded-2xl">
      <form className="flex h-full flex-col" onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true); setError(null);
        try {
          await p.onCreate({ customer: customer ? { ...(customer.id ? { id: customer.id } : {}), name: customer.name, phone: phone.trim() || null } : { name, phone: phone.trim() || null }, services: p.services.filter((x) => chosen.includes(x.id)), staffId, plate: plate.trim() || null });
        } catch (err) { setError((err as Error).message); setBusy(false); }
      }}>
        <div className="flex items-center justify-between border-b border-base px-5 py-3">
          <h2 className="text-xl font-bold">Senha {p.ticket} — cliente na fila</h2>
          <button type="button" aria-label="Fechar" onClick={p.onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            {customer
              ? <div className="flex h-12 items-center justify-between rounded-xl bg-surface-2 px-4 font-semibold">{customer.name}<button type="button" aria-label="Tirar o cliente" onClick={() => setCustomer(null)}><X size={16} /></button></div>
              : <input autoFocus required aria-label="Nome do cliente" placeholder="Nome do cliente" value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-xl border border-base bg-surface-2 px-4" />}
            <button type="button" onClick={() => setPicking(true)} className="h-12 rounded-xl border border-base px-4 font-medium">Cliente da ficha</button>
            <input aria-label="Telefone para avisar" inputMode="tel" placeholder="Telefone para avisar (opcional)" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 rounded-xl border border-base bg-surface-2 px-4 sm:col-span-2" />
            {p.plates && <input aria-label="Matrícula" placeholder="Matrícula (ex.: AAA-123-MC)" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} className="h-12 rounded-xl border border-base bg-surface-2 px-4 font-mono sm:col-span-2" />}
          </div>
          <fieldset>
            <legend className="mb-2 text-sm font-semibold">Quem atende</legend>
            <div className="flex flex-wrap gap-2">
              <button type="button" aria-pressed={staffId === null} onClick={() => setStaffId(null)} className={clsx('h-12 rounded-full border-2 px-4 font-medium', staffId === null ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>Primeiro livre</button>
              {p.staff.map((s) => (
                <button key={s.id} type="button" aria-label={s.name} aria-pressed={staffId === s.id} onClick={() => setStaffId(s.id)} className={clsx('flex h-12 items-center gap-2 rounded-full border-2 py-1 pl-1 pr-4 font-medium', staffId === s.id ? 'border-brand-700 bg-brand-50/60 dark:bg-white/5' : 'border-base')}>
                  <StaffAvatar s={s} size={34} />{s.name}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="mb-2 text-sm font-semibold">Serviços <span className="font-normal text-muted">(pode escolher depois, ao cobrar)</span></legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {list.map((x) => {
                const on = chosen.includes(x.id);
                const own = staffPrice(p.staff.find((s) => s.id === staffId), x.id);
                return (
                  <button key={x.id} type="button" aria-pressed={on} onClick={() => setChosen(on ? chosen.filter((c) => c !== x.id) : [...chosen, x.id])}
                    className={clsx('flex min-h-16 flex-col justify-between rounded-xl border-2 p-2.5 text-left text-sm', on ? 'border-brand-700 bg-brand-50/60 dark:bg-white/5' : 'border-base')}>
                    <span className="font-medium leading-tight">{x.shortName || x.name}</span>
                    <span className="num text-xs text-muted">{formatMoney(own?.price ?? x.unitPrice)} MT{(own?.durationMin ?? x.durationMin) ? ` · ${own?.durationMin ?? x.durationMin} min` : ''}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>
          {error && <p role="alert" className="rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
        </div>
        <div className="border-t border-base p-4">
          <button disabled={busy || (!customer && !name.trim())} className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-brand-700 text-lg font-bold text-white disabled:opacity-40">
            <Plus size={20} /> Pôr na fila{mins ? ` · ${mins} min` : ''}
          </button>
        </div>
      </form>
      {picking && <CustomerDialog store={p.store} who={identity(p.device)} onClose={() => setPicking(false)} onPick={(c) => { setCustomer(c); setPicking(false); }} />}
    </dialog>
  );
}
